










































































import { Component, Vue, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import { AppModule } from '@/store/modules/app';
import { UserModule } from '@/store/modules/user';
import { MessageModule, MessageUnreadStatus } from '@/store/modules/message';
import LangSelect from '@/components/global/LangSelect/index.vue';
import GlobalProgress from '@/components/global/Progress/index.vue';
import ThemeSelect from '@/components/global/ThemeSelect/index.vue';
import { translateViewResource } from '@/utils/index';
import {
  validateUserTokenExisted,
  validateUserTokenNotExpired,
} from '@/router/permission';
import { PermissionModule } from '@/store/modules/permission';
import { AccountTypeEnum } from '@/api/types';
import { configureRoutesAfterLoggedIn } from '@/utils/permission';

@Component({
  name: 'Login',
  components: {
    LangSelect,
    GlobalProgress,
    ThemeSelect,
  },
})
export default class extends Vue {
  private loginFormValid = false;
  private username = '';
  private usernameRules: ((v: string) => boolean | string)[] = [];
  private password = '';
  private passwordRules: ((v: string) => boolean | string)[] = [];
  private errorMessages = 'Incorrect login info';
  private snackbar = false;
  private showPassword = false;
  private loginBtnLoading = false;
  private redirect?: string;
  private otherQuery: any = {};

  get appLanguage() {
    return AppModule.language;
  }

  get tokenNotExisted() {
    return PermissionModule.userTokenPermission.tokenNotExisted;
  }

  get tokenExpired() {
    return PermissionModule.userTokenPermission.tokenExpired;
  }

  get unauthenticated() {
    return PermissionModule.userTokenPermission.unauthenticated;
  }

  @Watch('$route', { immediate: true })
  private onRouteChange(route: Route) {
    // TODO: remove the "as Dictionary<string>" hack after v4 release for vue-router
    // See https://github.com/vuejs/vue-router/pull/2050 for details
    const query = route.query as any;
    if (query) {
      this.redirect = query.redirect;
      this.otherQuery = this.getOtherQuery(query);
    }
  }

  @Watch('appLanguage')
  private onLangSelected() {
    (this.$refs.loginFormRef as any).resetValidation();
    this.autoFocus();
  }

  @Watch('tokenNotExisted', { immediate: true })
  private onTokenNotExistedChanged(value: boolean) {
    if (value) {
      this.$toast.error(
        this.translateViewResource('User not logged in') as string,
        {
          icon: 'mdi-account-question',
        }
      );
      this.$nextTick(() => {
        PermissionModule.ResetUserTokenPermission();
      });
    }
  }

  @Watch('unauthenticated', { immediate: true })
  private onTokenUnauthenticatedChanged(value: boolean) {
    if (value) {
      this.$toast.error(
        this.translateViewResource('User unauthenticated') as string,
        {
          icon: 'mdi-account-lock',
        }
      );
      this.$nextTick(() => {
        PermissionModule.ResetUserTokenPermission();
      });
    }
  }

  @Watch('tokenExpired', { immediate: true })
  private onTokenExpiredChanged(value: boolean) {
    if (value) {
      this.$toast.error(
        this.translateViewResource(
          'Your Session has expired, please relogin'
        ) as string,
        {
          icon: 'mdi-account-clock',
        }
      );
      this.$nextTick(() => {
        PermissionModule.ResetUserTokenPermission();
      });
    }
  }

  created() {
    this.usernameRules = [
      (v: string) =>
        !!v ||
        (this.translateViewResource('{msg} is required', {
          msg: this.$t('Username'),
        }) as string),
    ];
    this.passwordRules = [
      (v: string) =>
        !!v ||
        (this.translateViewResource('{msg} is required', {
          msg: this.$t('Password'),
        }) as string),
    ];
  }

  mounted() {
    this.autoFocus();
  }

  private autoFocus() {
    if (this.username === '') {
      (this.$refs.username as HTMLInputElement).focus();
    } else if (this.password === '') {
      (this.$refs.password as HTMLInputElement).focus();
    }
  }

  private createAccountTypeItems() {
    return [
      {
        text: this.$t('AccountTypeEnum.OCTB'),
        value: AccountTypeEnum.OCTB as string,
      },
      {
        text: this.$t('AccountTypeEnum.AGT'),
        value: AccountTypeEnum.AGT as string,
      },
      {
        text: this.$t('AccountTypeEnum.LINKTIVITY'),
        value: AccountTypeEnum.LINKTIVITY as string,
      },
    ];
  }

  private isRedirected() {
    return this.$route.fullPath !== '/login';
  }

  private async handleLogin() {
    if (!this.loginFormValid) {
      return;
    }
    AppModule.StartProgress();
    try {
      await UserModule.Login({
        username: this.username,
        password: this.password,
      });
      this.postLogin();
    } catch (err) {
      AppModule.StopProgress();
      this.$toast.error(
        this.$t('Failed to {do} due to {err}', {
          do: (this.$t('Login') as string).toLowerCase(),
          err: this.$t('Invalid username or password'),
        }) as string,
        {
          icon: 'mdi-account-alert',
        }
      );
    }
  }

  private postLogin() {
    configureRoutesAfterLoggedIn(this.$router);
    AppModule.StopProgress();
    MessageModule.SetMessageUnreadStatus(MessageUnreadStatus.LOADING);
    this.$router.push({
      path: this.redirect || '/',
      query: this.otherQuery,
    });
  }

  private getOtherQuery(query: any) {
    return Object.keys(query).reduce((acc, cur) => {
      if (cur !== 'redirect') {
        acc[cur] = query[cur];
      }
      return acc;
    }, {} as any);
  }

  private translateViewResource(
    resource: string,
    options?: { [key: string]: any }
  ) {
    return translateViewResource(
      this.$i18n,
      this.$options.name as string,
      resource,
      options
    );
  }
}
